/**
 * Redux async actions (src/store/actions/studentsActions.js).
 *
 * This module defines Redux Toolkit thunks (createAsyncThunk) that call backend endpoints via the shared axios client.
 * Slices consume the fulfilled payloads in `extraReducers` to update the UI state.
 */

import { createAction, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "../../helpers/axiosconfig";
import { clearAuthStorage, setAuthStorage } from "../../helpers/authStorage";
import { clearWebStoragePreservingConsent } from "../../helpers/logoutCleanup";
import { websocketConnected, websocketMessageReceived, websocketDisconnected } from '../slices/studentsSlice';
import { clearGhostData } from "../slices/ghostLoginSlice";
import { toAdminRequestError } from "../../utils/adminRequestError";

// import { toast } from "react-toastify";

// ACTIONS : api calls
/**
 * Async thunk: fetchAllstudents.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const fetchAllstudents = createAsyncThunk(
  "student/fetchAllStudent",
  async (params = {}, { rejectWithValue }) => {
    const queryParams = {};
    if (params.archiveScope) {
      queryParams.archiveScope = params.archiveScope;
    }

    try {
      const response = await axios.get(`FetchAll_students`, {
        params: queryParams,
      });
      return response.data.studentslist;
    } catch (error) {
      return rejectWithValue(toAdminRequestError(error, "Failed to fetch students"));
    }
  }
);

/**
 * Async thunk: setStudentArchiveStatus.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const setStudentArchiveStatus = createAsyncThunk(
  "student/setStudentArchiveStatus",
  async ({ studentId, isArchived }, { rejectWithValue }) => {
    try {
      const endpoint = isArchived ? "archive" : "unarchive";
      const response = await axios.patch(`admin/students/${studentId}/${endpoint}`);

      const student =
        response?.data?.entity ||
        response?.data?.student ||
        response?.data?.updatedStudent ||
        response?.data?.data?.student ||
        response?.data?.data ||
        null;

      return {
        studentId,
        isArchived,
        student,
      };
    } catch (error) {
      return rejectWithValue({
        studentId,
        isArchived,
        status: Number(error?.response?.status) || undefined,
        message:
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Failed to update student archive status",
      });
    }
  }
);

/**
 * Async thunk: Signup_Student.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const Signup_Student = createAsyncThunk(
  "student/Signup_Student",
  async (data, { rejectWithValue, dispatch }) => {
    try {
      // Retire any previous ghost transition before this authentication request.
      dispatch(clearGhostData());
      const response = await axios.post(`Signup_Student`, data);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response.data.message);
    }
  }
);

/**
 * Async thunk: FindUserByEmail.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const FindUserByEmail = createAsyncThunk(
  "student/FindUserByEmail",
  async (data) => {
    try {
      const response = await axios.post(`FindUserByEmail`, data);
      // 
      return response.data;
    } catch (error) {
      
      return error.response.data.message;
    }
  }
);

/**
 * Async thunk: MatchOTP.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const MatchOTP = createAsyncThunk("student/MatchOTP", async (data) => {
  try {
    const response = await axios.post(`MatchOTP`, data);
    // 
    return response.data;
  } catch (error) {
    
    return error.response.data.message;
  }
});

/**
 * Async thunk: Reset_Password.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const Reset_Password = createAsyncThunk(
  "auth/Reset_Password",
  async (data, { rejectWithValue }) => {
    try {
      const newPassword = String(
        data?.New_Password || data?.newPassword || data?.password || ""
      ).trim();
      const userId = String(data?.id || data?.userId || "").trim();
      const email = String(data?.Email || data?.email || "").trim();
      const confirmPassword = String(
        data?.confirmPassword || data?.Confirm_New_Password || newPassword
      ).trim();

      let response;

      // Authenticated password update (profile/security settings flow).
      if (userId && newPassword && !email) {
        response = await axios.post(`auth/set-password`, {
          userId,
          password: newPassword,
        });
      } else if (email && newPassword) {
        // OTP-based forgot password flow.
        response = await axios.post(`forgot-password/reset-password`, {
          email,
          New_Password: newPassword,
          confirmPassword,
        });
      } else {
        return rejectWithValue("Invalid password reset payload");
      }

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || "Password reset failed"
      );
    }
  }
);

/**
 * Async thunk: async_loaduser.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const async_loaduser = createAsyncThunk(
  "student/async_loaduser",
  async (expected, { rejectWithValue, fulfillWithValue, getState, requestId, signal }) => {
    const ownsBootstrap = () => !signal.aborted
      && getState().students?.authBootstrapRequestId === requestId;
    const retired = () => rejectWithValue({
      code: 'AUTH_BOOTSTRAP_RETIRED', message: 'Sign-in session request was superseded',
    });
    try {
      // Snapshot expectations before any await; callers cannot change the binding.
      const suppliedSession = expected?.expectedSession;
      const expectedSession = suppliedSession === undefined ? null : {
        id: suppliedSession?.id, role: suppliedSession?.role,
        mode: suppliedSession?.mode, requestId: suppliedSession?.requestId,
      };
      const hasExpectedMfa = expected?.expectedMfaUserId !== undefined
        || expected?.expectedMfaRole !== undefined;
      const expectedMfaId = expected?.expectedMfaUserId;
      const expectedMfaRole = expected?.expectedMfaRole;
      if ((suppliedSession !== undefined && (!expectedSession
        || typeof expectedSession.id !== 'string' || !/^[a-f0-9]{24}$/i.test(expectedSession.id)
        || !['student', 'teacher', 'admin', 'accountant', 'counsellor'].includes(expectedSession.role)
        || expectedSession.mode !== 'direct'
        || typeof expectedSession.requestId !== 'string' || !expectedSession.requestId))
        || (hasExpectedMfa && (typeof expectedMfaId !== 'string'
          || !/^[a-f0-9]{24}$/i.test(expectedMfaId)
          || !['teacher', 'admin'].includes(expectedMfaRole)))) {
        return rejectWithValue({ code: 'AUTH_BOOTSTRAP_EXPECTATION_INVALID', message: 'Invalid sign-in session expectation' });
      }
      // BUG-004/005: The auth token is an httpOnly cookie sent automatically
      // by the shared axios client. Non-sensitive userData and ghost metadata
      // are optional display hints, never proof that a session exists. In
      // particular, a valid session must survive client-storage cleanup.

      // Avoid hanging the entire app on boot if the backend (or DB) is unreachable.
      // This thunk is the gate for `authInitialized` in App.js/Layout.js.
      const timeoutMs = Number(process.env.REACT_APP_AUTH_BOOTSTRAP_TIMEOUT_MS) || 10000;
      const currentPath = typeof window !== 'undefined' ? window.location.pathname || '' : '';
      const isCounsellorRoute =
        currentPath === '/counsellor-login' || currentPath.startsWith('/Counsellor-Dashboard');
      const response = await axios.get(
        isCounsellorRoute ? 'v1/counsellor/profile' : 'me',
        timeoutMs > 0 ? { timeout: timeoutMs } : undefined
      );
      if (!ownsBootstrap()) return retired();

      const user = response.data.user || response.data.counsellor;
      // Only the authenticated server envelope can establish inbox identity/mode.
      const session = response.data.session;
      const canonicalSession = response.data.success === true
        && session && typeof session.userId === 'string'
        && /^[a-f0-9]{24}$/i.test(session.userId)
        && session.userId === user?._id && session.userType === user?.UserType
        && ['student', 'teacher', 'admin', 'accountant', 'counsellor'].includes(session.userType)
        && ['direct', 'ghost'].includes(session.mode)
        ? { userId: session.userId, userType: session.userType, mode: session.mode }
        : null;

      // Refuse a different cookie principal/mode before language PUTs,
      // preference storage or a fulfilled identity can escape this thunk.
      if ((expectedSession && (!canonicalSession
        || canonicalSession.userId !== expectedSession.id
        || canonicalSession.userType !== expectedSession.role
        || canonicalSession.mode !== expectedSession.mode))
        || (hasExpectedMfa && (!canonicalSession
          || canonicalSession.userId !== expectedMfaId
          || canonicalSession.userType !== expectedMfaRole
          || canonicalSession.mode !== 'direct'))) {
        return rejectWithValue({ code: 'AUTH_BOOTSTRAP_IDENTITY_MISMATCH', message: 'Sign-in session does not match the authenticated response' });
      }

      // A public/expired session can occasionally return a successful but
      // empty payload (for example while the auth proxy is recovering). Treat
      // it as an unauthenticated bootstrap result instead of dereferencing
      // UserType and emitting a runtime error on every public route.
      if (!user || typeof user !== 'object' || !user.UserType) {
        return rejectWithValue({ message: 'Authenticated user not found' });
      }

      // Best-effort: keep server-side locale in sync with an explicit UI preference.
      // This matters for backend-generated notifications (e.g. lesson reminders).
      try {
        if (user?.UserType === 'student') {
          const storedUiLang = localStorage.getItem('speakable-language') || localStorage.getItem('i18nextLng');
          const uiLang = storedUiLang ? String(storedUiLang).split('-')[0].toLowerCase() : '';
          const serverLang = String(user?.language || '').split('-')[0].toLowerCase();

          if (uiLang && uiLang !== serverLang && user?._id) {
            const updateRes = await axios.put(`Update_Student/${user._id}`, { language: uiLang });
            if (!ownsBootstrap()) return retired();
            if (updateRes?.data?.success) {
              user.language = uiLang;
            }
          } else if (!storedUiLang && serverLang) {
            // Seed localStorage from the server preference (don't override server using defaults).
            localStorage.setItem('speakable-language', serverLang);
            localStorage.setItem('i18nextLng', serverLang);
          }
        }
      } catch (err) {
        // Best-effort only; do not block auth bootstrap.
        if (!ownsBootstrap()) return retired();
        console.warn('[auth] Failed to sync user language preference:', err?.message || err);
      }
      if (!ownsBootstrap()) return retired();
      
      // Set teacher preferences to localStorage for teachers
      if (user.UserType === 'teacher') {
        if (user.userTimezone) {
          localStorage.setItem('selectedTimezone', user.userTimezone);
        }
        if (user.hourFormat) {
          localStorage.setItem('hourFormat', user.hourFormat);
        }
      }
      
      return fulfillWithValue(user, { canonicalSession });
    } catch (error) {
      if (!ownsBootstrap()) return retired();
      const status = error.response?.status;
      if (status === 401 || status === 403) {
        clearAuthStorage();
      }
      if (status !== 401 && status !== 403) {
        console.error("Error in async_loaduser:", error);
      }
      return rejectWithValue(error.response?.data || { message: error.message });
    }
  },
  {
    condition: (expected, { getState }) => {
      if (expected?.revalidationRequestId === undefined) return true;
      const state = getState().students;
      const requestId = expected.revalidationRequestId;
      // Retired recovery callers must not even acquire pending ownership and
      // clear a successor's proof. Never revalidate before the sent POST settles.
      return typeof requestId === 'string' && requestId.length > 0
        && state?.canonicalBootstrapRequired?.revalidate === true
        && state.canonicalBootstrapRequired.requestId === requestId
        && state.ghostRecovery?.requestId === requestId
        && state.ghostRecovery.transportSettled === true
        && state.ghostRecovery.needsRecovery === true
        && state.isAuthenticated && !state.authLoading && !state.ghostTransitionRequestId;
    },
  }
);

/**
 * Async thunk: async_removeuser.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const async_removeuser = createAsyncThunk(
  "student/async_removeuser",
  async (_, { dispatch }) => {
    // A failed/uncertain revocation must retain the credential and UI state so
    // the user can retry. Let Axios reject the request before any local cleanup.
    const response = await axios.get(`signout`, { timeout: 5000 });
    clearAllStorageAndCookies();
    dispatch(clearGhostData());
    return response;
  }
);

// Helper function to clear all localStorage, sessionStorage, and cookies
/**
 * Clear All Storage And Cookies.
 *
 * Named helper used to keep this module readable and testable.
 */
const clearAllStorageAndCookies = () => {
  // Clear all web storage, but preserve cookie consent state (device/browser scoped).
  try {
    clearWebStoragePreservingConsent();
  } catch (e) {
    // Fallback for unusual environments.
    try {
      localStorage?.clear();
    } catch (e2) {}
    try {
      sessionStorage?.clear();
    } catch (e3) {}
  }

  // Clear all cookies
  try {
    const preserveCookies = new Set(['cmpConsentState', 'cookieConsent']);
    document.cookie.split(";").forEach(function (c) {
      const eqPos = c.indexOf("=");
      const name = eqPos > -1 ? c.substring(0, eqPos).trim() : c.trim();
      if (name && !preserveCookies.has(name)) {
        document.cookie =
          name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;";
        document.cookie =
          name +
          "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=" +
          window.location.hostname +
          ";";
      }
    });
  } catch (e) {}
};

/**
 * Async thunk: fetchStudentDetails.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const fetchStudentDetails = createAsyncThunk(
  "student/fetchStudentDetails",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.get(`fetchStudentDetails/${id}`);
      const studentDetails = response.data?.StudentDetails;
      if (!studentDetails || typeof studentDetails !== "object") {
        return rejectWithValue(
          toAdminRequestError(
            { response: { status: 404, data: response.data } },
            "Student details unavailable"
          )
        );
      }
      return studentDetails;
    } catch (error) {
      return rejectWithValue(toAdminRequestError(error, "Student details unavailable"));
    }
  }
);

/**
 * Async thunk: DeleteStudent.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const DeleteStudent = createAsyncThunk(
  "student/DeleteStudent",
  async (student_ID, { rejectWithValue }) => {
    try {
      const response = await axios.delete(`Delete_student/${student_ID}`);
      // 
      return response.data;
    } catch (error) {
      return rejectWithValue(toAdminRequestError(error, "Failed to delete student"));
    }
  }
);

/**
 * Async thunk: updateStudent.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const updateStudent = createAsyncThunk(
  "student/updateStudent",
  async ({ student_ID, updatedData }, { rejectWithValue }) => {
    try {
      

      const response = await axios.put(
        `Update_Student/${student_ID}`,
        updatedData
      );
      
      if (response.data.success) {
        return {
          ...response.data,
          student_ID,
          updatedFields: updatedData
        };
      } else {
        return rejectWithValue(response.data.message || 'Update failed');
      }
    } catch (error) {
      console.error("Update error:", error);
      return rejectWithValue(toAdminRequestError(error, "Failed to update student"));
    }
  }
);

/**
 * Async thunk: Signin_user.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const Signin_user = createAsyncThunk(
  "student/Signin_user",
  async (data, { rejectWithValue, dispatch }) => {
    try {
      // Retire any previous ghost transition before this authentication request.
      dispatch(clearGhostData());
      const { rememberMe = true, ...payload } = data || {};
      const response = await axios.post(`Signin_user`, payload);
      
      
      if (response.data.token) {
        // BUG-004/005: Token is set as httpOnly cookie by the backend.
        // Only store non-sensitive user metadata for UI display.
        setAuthStorage({
          userData: {
            _id: response.data.data._id,
            Username: response.data.data.Username,
            UserType: response.data.data.UserType,
            Email: response.data.data.Email
          },
          rememberMe
        });

        // Automatically set teacher preferences to localStorage if user is a teacher
        if (response.data.data.UserType === 'teacher' && response.data.data.userTimezone) {
          localStorage.setItem('selectedTimezone', response.data.data.userTimezone);
        }
        if (response.data.data.UserType === 'teacher' && response.data.data.hourFormat) {
          localStorage.setItem('hourFormat', response.data.data.hourFormat);
        }

        // Defensive cleanup: normal sign-in must never retain stale ghost mode.
        dispatch(clearGhostData());
      }
      
      return response.data;
    } catch (error) {
      console.error("Login error:", error.response?.data);
      return rejectWithValue(error.response?.data || { message: error.message });
    }
  }
);

/**
 * Async thunk: Signup_Student_By_Admin.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const Signup_Student_By_Admin = createAsyncThunk(
  "student/Signup_Student_By_Admin",
  async (data, { rejectWithValue }) => {
    try {
      
        const response = await axios.post(`Signup_Student_By_Admin`, data);
        return response;
    } catch (error) {
       return rejectWithValue(toAdminRequestError(error, "Failed to create student"));
    }
  }
);



/**
 * Async thunk: Signup_Student_With_Booking.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const Signup_Student_With_Booking = createAsyncThunk(
  "student/Signup_Student_With_Booking",
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.post(`Signup_Student_With_Booking`, data);
      return response.data;
    } catch (error) {
      // Properly reject with the error so it can be caught in the component
      if (error.response && error.response.data) {
        return rejectWithValue({ ...error.response.data, status: error.response.status });
      }
      return rejectWithValue({ message: error.message });
    }
  }
);



/**
 * Async thunk: SearchStudentbyUsername.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const SearchStudentbyUsername = createAsyncThunk(
  "student/SearchStudentbyUsername",
  async (input) => {
    // 
    try {
      const response = await axios.post(`SearchStudentbyUsername/${input}`);
      // 
      return response;
    } catch (error) {
      
      return error.response.data.message;
    }
  }
);

/**
 * Async thunk: Configure_RecieveEmail.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const Configure_RecieveEmail = createAsyncThunk(
  "student/Configure_RecieveEmail",
  async (Email)=> {
    

    try {
      await axios.get(`Configure_RecieveEmail/${Email}`);
      
      // toast.success(response.data.message)
      // return response;
    } catch (error) {
      // toast.error(error.response.data.message)
      
      return error.response.data.message;
    }
  }
);

/**
 * Async thunk: listDriveFiles.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const listDriveFiles = createAsyncThunk(
  "student/listDriveFiles",
  async (folderId, { rejectWithValue }) => {
    try {
      const response = await axios.get(`list-drive-files/${folderId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

/**
 * Async thunk: downloadDriveFile.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const downloadDriveFile = createAsyncThunk(
  "student/downloadDriveFile",
  async (fileId, { rejectWithValue }) => {
    try {
      const response = await axios.get(`download-drive-file/${fileId}`, {
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

/**
 * Async thunk: updatePhoneNumber.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const updatePhoneNumber = createAsyncThunk(
  "student/updatePhoneNumber",
  async ({ userId, Phone_Number }, { rejectWithValue }) => {
    try {
      const response = await axios.post('/update-phone-number', { userId, Phone_Number });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

/**
 * Async thunk: initializeWebSocket.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const initializeWebSocket = createAsyncThunk(
  "student/initializeWebSocket",
  async (_, { rejectWithValue }) => {
    try {
      // BUG-004/005: Auth token is in httpOnly cookie, sent automatically via withCredentials.
      const response = await axios.get('ws/init', {
        maxBodyLength: Infinity
      });
      return response.data;
    } catch (error) {
      console.error("WebSocket initialization error:", error);
      return rejectWithValue(error.response?.data || { message: error.message });
    }
  }
);

/**
 * Async thunk: connectWebSocket.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const connectWebSocket = createAsyncThunk(
  'student/connectWebSocket',
  async (userId, { dispatch }) => {
    const { io } = await import('socket.io-client');
    const socketUrl = process.env.REACT_APP_API_URL || 'https://speakable.online';
    const socket = io(socketUrl, {
      query: { userId },
      withCredentials: true,
      transports: ['polling', 'websocket']
    });

    socket.on('connect', () => {
      
      dispatch(websocketConnected());
    });

    socket.on('message', (data) => {
      dispatch(websocketMessageReceived(data));
    });

    socket.on('disconnect', (reason) => {
     
      dispatch(websocketDisconnected());
    });

    return socket;
  }
);

/**
 * Async thunk: sendWebSocketMessage.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const sendWebSocketMessage = createAsyncThunk(
  'student/sendWebSocketMessage',
  async (message, { getState }) => {
    const { socket } = getState().students;
    if (socket && socket.connected) {
      socket.emit('message', message);
    } else {
      throw new Error('WebSocket is not connected');
    }
  }
);

/**
 * Async thunk: disconnectWebSocket.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const disconnectWebSocket = createAsyncThunk(
  'student/disconnectWebSocket',
  async (_, { getState }) => {
    const { socket } = getState().students;
    if (socket) {
      socket.close();
    }
  }
);

/**
 * Async thunk: updateStudentTimezone.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const updateStudentTimezone = createAsyncThunk(
  "student/updateStudentTimezone",
  async ({ userId, Timezone }, { rejectWithValue }) => {
    try {
      // Keep the thunk aligned with the authenticated student contract exposed
      // by the backend: PUT /api/update-timezone.
      const response = await axios.put('/update-timezone', { userId, Timezone });
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data || {
        message: 'Nie udało się zaktualizować strefy czasowej.',
      });
    }
  }
);

/**
 * Async thunk: googleLogin.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const googleLoginReady = createAction('student/googleLoginReady');
export const googleMfaRetired = createAction('student/googleMfaRetired');

const waitForGoogleStatus = signal => new Promise((resolve, reject) => {
  const stop = () => { clearTimeout(timer); signal.removeEventListener('abort', stop); reject(new Error('Google sign-in retired')); };
  const timer = setTimeout(() => { signal.removeEventListener('abort', stop); resolve(); }, 500);
  if (signal.aborted) stop();
  else signal.addEventListener('abort', stop, { once: true });
});

export const googleLogin = createAsyncThunk(
  "student/googleLogin",
  async (_, { rejectWithValue, dispatch, getState, requestId, signal }) => {
    const owns = () => !signal.aborted && getState().students?.googleLoginRequestId === requestId;
    const retired = () => rejectWithValue({ code: 'GOOGLE_LOGIN_RETIRED', message: 'Google sign-in was superseded' });
    // Existing axios support lets this auth flow handle its own 401 denial;
    // server authentication/CSRF checks still run, without a global page reload
    // discarding the signup/modal/booking owner on an expired attempt.
    const transport = { signal, timeout: 10000, skipAuthRedirect: true };
    let bootstrap;
    const abortBootstrap = () => {
      if (bootstrap && getState().students?.authBootstrapRequestId === bootstrap.requestId) bootstrap.abort();
    };
    try {
      // This cleanup belongs to the pending Google request, not a new auth transition.
      dispatch(clearGhostData({ googleRequestId: requestId }));
      // The shared axios client only echoes CSRF cookies; bootstrap one before
      // the protected write even on a fresh first visit. This is not login proof.
      await axios.get('auth/google/transaction', transport);
      if (!owns()) return retired();
      const { data: start } = await axios.post('auth/google/transaction', {}, transport);
      if (!owns()) return retired();
      if (start?.success !== true || typeof start.attemptId !== 'string' || !/^[a-f0-9]{64}$/.test(start.attemptId)) {
        throw new Error('Invalid Google sign-in transaction');
      }
      const attemptId = start.attemptId;
      dispatch(googleLoginReady({ requestId, attemptId }));
      const deadline = Date.now() + 2 * 60 * 1000;
      while (true) {
        if (!owns()) return retired();
        const { data: status } = await axios.get('auth/google/status', { ...transport, params: { attemptId } });
        if (!owns()) return retired();
        if (status?.success === true && status.verified === true && status.attemptId === attemptId) break;
        if (status?.pending !== true || Date.now() >= deadline) throw new Error('Google sign-in did not complete');
        await waitForGoogleStatus(signal);
      }
      // An old cookie, popup message or raw profile can never enter this path.
      const { data: result } = await axios.post('auth/google/callback', { attemptId }, transport);
      if (!owns()) return retired();
      if (result?.requires2FA === true) {
        if (result.success !== false || typeof result.challengeId !== 'string' || !/^[a-f0-9]{64}$/.test(result.challengeId)
          || typeof result.userId !== 'string' || !/^[a-f0-9]{24}$/i.test(result.userId)
          || !['admin', 'teacher'].includes(result.userType)) throw new Error('Invalid sign-in challenge');
        return { ...result, attemptId };
      }
      const user = result?.data;
      if (result?.success !== true || result.token !== true
        || typeof user?._id !== 'string' || !/^[a-f0-9]{24}$/i.test(user._id)
        || !['student', 'teacher', 'admin'].includes(user.UserType)) throw new Error('Google sign-in was not verified');
      bootstrap = dispatch(async_loaduser({ expectedSession: {
        id: user._id, role: user.UserType, mode: 'direct', requestId,
      } }));
      signal.addEventListener('abort', abortBootstrap, { once: true });
      if (!owns()) { abortBootstrap(); return retired(); }
      const sessionUser = await bootstrap.unwrap();
      if (!owns()) return retired();
      const state = getState().students;
      if (!state.isAuthenticated || state.authLoading || state.canonicalSession?.mode !== 'direct'
        || state.canonicalSession.userId !== user._id || state.canonicalSession.userType !== user.UserType
        || sessionUser?._id !== user._id || sessionUser.UserType !== user.UserType) return retired();
      setAuthStorage({ userData: {
        _id: sessionUser._id, Username: sessionUser.Username, UserType: sessionUser.UserType, Email: sessionUser.Email,
      }, rememberMe: true });
      return { success: true, token: true, data: sessionUser, attemptId };
    } catch (error) {
      if (!owns()) return retired();
      return rejectWithValue(error.response?.data || { message: error.message });
    } finally {
      signal.removeEventListener('abort', abortBootstrap);
    }
  }
);

// Set password after OAuth login
/**
 * Async thunk: setPasswordAfterOAuth.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const setPasswordAfterOAuth = createAsyncThunk(
  "student/setPasswordAfterOAuth",
  async ({ userId, password }, { rejectWithValue }) => {
    try {
      const response = await axios.post('auth/set-password', { userId, password });
      return response.data;
    } catch (error) {
      console.error("Set password error:", error.response?.data);
      return rejectWithValue(error.response?.data || { message: error.message });
    }
  }
);

// Start phone verification required for new Google-created student accounts.
export const recordGooglePhoneVerificationModalShown = createAsyncThunk(
  "student/recordGooglePhoneVerificationModalShown",
  async ({ context = "account", modalInstanceId = "", hasPrefilledPhone = false } = {}, { rejectWithValue }) => {
    try {
      const response = await axios.post('auth/phone-verification/modal-shown', {
        context,
        modalInstanceId,
        hasPrefilledPhone,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: error.message });
    }
  }
);

export const startGooglePhoneVerification = createAsyncThunk(
  "student/startGooglePhoneVerification",
  async ({ method = "whatsapp", Phone_Number }, { rejectWithValue }) => {
    try {
      const response = await axios.post('auth/phone-verification/start', {
        method,
        Phone_Number,
      });
      return response.data;
    } catch (error) {
      console.error("Start phone verification error:", error.response?.data);
      return rejectWithValue(error.response?.data || { message: error.message });
    }
  }
);

// Confirm the phone verification OTP and clear the required Google-account flag.
export const confirmGooglePhoneVerification = createAsyncThunk(
  "student/confirmGooglePhoneVerification",
  async ({ challengeId, otp }, { rejectWithValue }) => {
    try {
      const response = await axios.post('auth/phone-verification/confirm', {
        challengeId,
        otp,
      });
      return response.data;
    } catch (error) {
      console.error("Confirm phone verification error:", error.response?.data);
      return rejectWithValue(error.response?.data || { message: error.message });
    }
  }
);
